import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './SensitiveDeleteConfig.css';
import { useProtectedDataLock } from '../../context/SensitiveDataLockContext';

const API_URL = import.meta.env.VITE_API_URL;

const SensitiveDeleteConfig = () => {
    const [activeTab, setActiveTab] = useState('actions'); // 'actions' | 'platform' | 'password' | 'history' | 'exceptional'
    const [actions, setActions] = useState([]);
    const [security, setSecurity] = useState({
        hasPasswordSet: false,
        isLockedOut: false,
        lockoutRemainingMinutes: 0,
        failedAttemptsCount: 0,
        maxFailedAttempts: 5
    });
    const [platformControl, setPlatformControl] = useState({
        maintenanceMode: false,
        allowAdminBypass: true,
        globalForceLogoutAt: null,
        lastFlushedAt: null,
        lastFlushedBy: null
    });
    const [history, setHistory] = useState([]);
    const [exceptionalAuths, setExceptionalAuths] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedModule, setSelectedModule] = useState('ALL');
    const [selectedLevel, setSelectedLevel] = useState('ALL');
    const [selectedType, setSelectedType] = useState('ALL');
    const [authFilterStatus, setAuthFilterStatus] = useState('ALL');

    // Modals
    const [editActionModal, setEditActionModal] = useState({ isOpen: false, action: null });
    const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [flushingSessions, setFlushingSessions] = useState(false);
    const [togglingMaint, setTogglingMaint] = useState(false);

    // Password Change Form
    const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [changingPwd, setChangingPwd] = useState(false);

    // Exceptional Authorizations Form & Modal
    const [isCreateAuthOpen, setIsCreateAuthOpen] = useState(false);
    const [revokeAuthModal, setRevokeAuthModal] = useState({ isOpen: false, authId: null, reason: '', protectionPassword: '' });
    const [newAuthForm, setNewAuthForm] = useState({
        userId: '',
        actionCodes: [],
        modules: [],
        bypassSecretCode: false,
        bypassMaintenance: false,
        enableDisabledFeatures: false,
        permanent: false,
        startAt: '',
        expiresAt: '',
        reason: '',
        protectionPassword: ''
    });

    const { fetchProtectionConfig } = useProtectedDataLock();

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    const loadAllData = async () => {
        try {
            setLoading(true);
            const [actionsRes, historyRes, platformRes, authsRes, usersRes] = await Promise.all([
                axios.get(`${API_URL}/system-settings/sensitive-actions`, getAuthHeaders()),
                axios.get(`${API_URL}/system-settings/sensitive-actions/history`, getAuthHeaders()),
                axios.get(`${API_URL}/system-settings/platform-control`, getAuthHeaders()),
                axios.get(`${API_URL}/system-settings/exceptional-authorizations`, getAuthHeaders()).catch(() => ({ data: { data: [] } })),
                axios.get(`${API_URL}/users`, getAuthHeaders()).catch(() => ({ data: { users: [] } }))
            ]);

            if (actionsRes.data?.success && actionsRes.data?.data) {
                setActions(actionsRes.data.data.actions || []);
                setSecurity(actionsRes.data.data.security || {});
            }

            if (historyRes.data?.success && Array.isArray(historyRes.data?.data)) {
                setHistory(historyRes.data.data);
            }

            if (platformRes.data?.success && platformRes.data?.data) {
                setPlatformControl(platformRes.data.data);
            }

            if (authsRes.data?.success && Array.isArray(authsRes.data?.data)) {
                setExceptionalAuths(authsRes.data.data);
            }

            const rawUsers = usersRes.data?.users || usersRes.data?.data || (Array.isArray(usersRes.data) ? usersRes.data : []);
            setUsersList(rawUsers);
        } catch (err) {
            console.error('Erreur chargement données actions sensibles:', err);
            Swal.fire('Erreur', 'Impossible de charger la configuration globale des actions sensibles.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAllData();
    }, []);

    // Filter Actions
    const filteredActions = useMemo(() => {
        return actions.filter(action => {
            const matchesQuery = searchQuery === '' ||
                action.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                action.actionCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (action.description && action.description.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesModule = selectedModule === 'ALL' || action.module === selectedModule;
            const matchesLevel = selectedLevel === 'ALL' || action.level === selectedLevel;
            const matchesType = selectedType === 'ALL' || action.type === selectedType;

            return matchesQuery && matchesModule && matchesLevel && matchesType;
        });
    }, [actions, searchQuery, selectedModule, selectedLevel, selectedType]);

    // Filter Exceptional Authorizations
    const filteredExceptionalAuths = useMemo(() => {
        const now = new Date();
        return exceptionalAuths.filter(item => {
            const isCurrentlyActive = item.isActive && (item.permanent || (item.expiresAt && new Date(item.expiresAt) > now));
            const isCurrentlyExpired = item.isActive && !item.permanent && item.expiresAt && new Date(item.expiresAt) <= now;
            const isCurrentlyRevoked = !item.isActive;

            if (authFilterStatus === 'active') return isCurrentlyActive;
            if (authFilterStatus === 'expired') return isCurrentlyExpired;
            if (authFilterStatus === 'revoked') return isCurrentlyRevoked;
            return true;
        });
    }, [exceptionalAuths, authFilterStatus]);

    // Quick Toggle Action
    const handleQuickToggleAction = async (action, field, newValue) => {
        const { value: password } = await Swal.fire({
            title: 'Code Secret Requis',
            text: `Veuillez saisir le code secret de protection pour modifier l'action "${action.label}".`,
            input: 'password',
            inputPlaceholder: 'Saisissez votre code secret de protection',
            showCancelButton: true,
            confirmButtonText: 'Valider et Enregistrer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#1e3a5f'
        });

        if (!password) return;

        try {
            setSaving(true);
            const response = await axios.put(
                `${API_URL}/system-settings/sensitive-actions/${action.actionCode}`,
                {
                    currentPassword: password,
                    updatedConfig: { [field]: newValue }
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Action Mise à Jour',
                    text: `La règle "${action.label}" a été modifiée avec succès.`,
                    timer: 2000,
                    showConfirmButton: false
                });
                fetchProtectionConfig();
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur mise à jour rapide action:', err);
            const msg = err.response?.data?.message || 'Erreur lors de la mise à jour de l\'action.';
            Swal.fire('Erreur de Sécurité', msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Save Detailed Action Rules
    const handleSaveDetailedAction = async (e) => {
        e.preventDefault();
        if (!confirmPasswordInput) {
            Swal.fire('Attention', 'Le code secret est obligatoire pour enregistrer les modifications.', 'warning');
            return;
        }

        try {
            setSaving(true);
            const response = await axios.put(
                `${API_URL}/system-settings/sensitive-actions/${editActionModal.action.actionCode}`,
                {
                    currentPassword: confirmPasswordInput,
                    updatedConfig: editActionModal.action
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Règles Enregistrées',
                    text: `Les configurations pour ${editActionModal.action.actionCode} ont été sauvegardées.`,
                    timer: 2000,
                    showConfirmButton: false
                });
                setEditActionModal({ isOpen: false, action: null });
                setConfirmPasswordInput('');
                fetchProtectionConfig();
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur sauvegarde détaillée action:', err);
            const msg = err.response?.data?.message || 'Erreur lors de l\'enregistrement des règles.';
            Swal.fire('Erreur', msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Toggle Maintenance Mode
    const handleToggleMaintenanceMode = async () => {
        const nextMode = !platformControl.maintenanceMode;
        const confirmResult = await Swal.fire({
            title: nextMode ? '🚨 Activer le Mode Maintenance ?' : '🟢 Désactiver le Mode Maintenance ?',
            text: nextMode ?
                'Toutes les interfaces de l’application seront verrouillées pour les utilisateurs normaux.' :
                'Le mode maintenance sera levé et tous les utilisateurs pourront de nouveau accéder aux fonctions.',
            icon: 'warning',
            input: 'password',
            inputPlaceholder: 'Entrez le code secret de protection',
            showCancelButton: true,
            confirmButtonText: nextMode ? 'Oui, Activer la Maintenance' : 'Oui, Désactiver la Maintenance',
            cancelButtonText: 'Annuler',
            confirmButtonColor: nextMode ? '#dc2626' : '#059669'
        });

        if (!confirmResult.isConfirmed || !confirmResult.value) return;

        try {
            setTogglingMaint(true);
            const response = await axios.put(
                `${API_URL}/system-settings/platform-control/maintenance`,
                {
                    currentPassword: confirmResult.value,
                    enabled: nextMode,
                    maintenanceMode: nextMode
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Mode Maintenance Mis à Jour',
                    text: response.data.message,
                    timer: 2500,
                    showConfirmButton: false
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur toggle maintenance:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors du basculement du mode maintenance', 'error');
        } finally {
            setTogglingMaint(false);
        }
    };

    // Toggle Paiements & Échéances Maintenance Mode
    const handleTogglePaiementMaintenanceMode = async () => {
        const nextMode = !platformControl.paiementEcheanceMaintenance;
        const confirmResult = await Swal.fire({
            title: nextMode ? '🚨 Activer Maintenance Paiements & Échéances ?' : '🟢 Désactiver Maintenance Paiements & Échéances ?',
            text: nextMode ?
                'Le module Paiements & Échéances sera verrouillé pour les utilisateurs normaux.' :
                'Le module Paiements & Échéances sera déverrouillé et réaccessible.',
            icon: 'warning',
            input: 'password',
            inputPlaceholder: 'Entrez le code secret de protection',
            showCancelButton: true,
            confirmButtonText: nextMode ? 'Oui, Activer la Maintenance' : 'Oui, Désactiver la Maintenance',
            cancelButtonText: 'Annuler',
            confirmButtonColor: nextMode ? '#dc2626' : '#059669'
        });

        if (!confirmResult.isConfirmed || !confirmResult.value) return;

        try {
            setTogglingMaint(true);
            const response = await axios.put(
                `${API_URL}/system-settings/platform-control/maintenance`,
                {
                    currentPassword: confirmResult.value,
                    paiementEcheanceMaintenance: nextMode,
                    targetModule: 'paiements'
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Maintenance Paiements Mis à Jour',
                    text: response.data.message,
                    timer: 2500,
                    showConfirmButton: false
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur toggle maintenance paiements:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors du basculement du mode maintenance', 'error');
        } finally {
            setTogglingMaint(false);
        }
    };

    const handlePurgeRevokedAuths = async () => {
        const confirmResult = await Swal.fire({
            title: 'Purger toutes les autorisations révoquées ?',
            text: 'Cette action supprimera définitivement toutes les autorisations révoquées de la base de données.',
            icon: 'warning',
            input: 'password',
            inputPlaceholder: 'Entrez le code secret de protection',
            showCancelButton: true,
            confirmButtonText: 'Oui, Purger Définitivement',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#dc2626'
        });

        if (!confirmResult.isConfirmed || !confirmResult.value) return;

        try {
            const response = await axios.post(
                `${API_URL}/system-settings/exceptional-authorizations/revoked/purge`,
                { protectionPassword: confirmResult.value },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Purge Réussie',
                    text: response.data.message,
                    timer: 2500,
                    showConfirmButton: false
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur purge autorisations révoquées:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la purge', 'error');
        }
    };

    const handleClearHistory = async () => {
        const confirmResult = await Swal.fire({
            title: 'Vider l\'historique des modifications ?',
            text: 'Voulez-vous supprimer toutes les entrées de l\'historique des modifications des politiques de sécurité ?',
            icon: 'warning',
            input: 'password',
            inputPlaceholder: 'Entrez le code secret de protection',
            showCancelButton: true,
            confirmButtonText: 'Oui, Vider l\'Historique',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#dc2626'
        });

        if (!confirmResult.isConfirmed || !confirmResult.value) return;

        try {
            const response = await axios.post(
                `${API_URL}/system-settings/config-history/clear`,
                { protectionPassword: confirmResult.value },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Historique Vidé',
                    text: response.data.message,
                    timer: 2500,
                    showConfirmButton: false
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur clear history:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors du vidage de l\'historique', 'error');
        }
    };

    // Flush All Sessions
    const handleFlushAllSessions = async () => {
        const confirmResult = await Swal.fire({
            title: '🚨 Vider TOUTES les sessions actives ?',
            text: 'Cette action annulera immédiatement tous les jetons d’accès actifs et déconnectera tous les utilisateurs.',
            icon: 'warning',
            input: 'password',
            inputPlaceholder: 'Entrez le code secret de protection pour confirmer',
            showCancelButton: true,
            confirmButtonText: 'Oui, Réinitialiser Toutes les Sessions',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#dc2626'
        });

        if (!confirmResult.isConfirmed || !confirmResult.value) return;

        try {
            setFlushingSessions(true);
            const response = await axios.post(
                `${API_URL}/system-settings/platform-control/flush-sessions`,
                { currentPassword: confirmResult.value },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sessions Purgées !',
                    text: response.data.message,
                    confirmButtonText: 'Parfait'
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur flushAllSessions:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Impossible de vider les sessions.', 'error');
        } finally {
            setFlushingSessions(false);
        }
    };

    // Change Password
    const handleChangeProtectionPassword = async (e) => {
        e.preventDefault();
        if (pwdForm.newPassword !== pwdForm.confirmPassword) {
            Swal.fire('Attention', 'Le nouveau mot de passe et la confirmation ne correspondent pas.', 'warning');
            return;
        }

        try {
            setChangingPwd(true);
            const response = await axios.put(
                `${API_URL}/system-settings/change-protection-password`,
                {
                    currentPassword: pwdForm.currentPassword,
                    newPassword: pwdForm.newPassword
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Code Secret Mis à Jour',
                    text: 'Le code secret de protection a été modifié avec succès.',
                    confirmButtonText: 'OK'
                });
                setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur modification mot de passe:', err);
            Swal.fire('Erreur de Sécurité', err.response?.data?.message || 'Erreur lors de la modification du code secret.', 'error');
        } finally {
            setChangingPwd(false);
        }
    };

    // Create Exceptional Authorization
    const handleCreateExceptionalAuthSubmit = async (e) => {
        e.preventDefault();
        if (!newAuthForm.userId) {
            Swal.fire('Attention', 'Veuillez sélectionner un utilisateur bénéficiaire.', 'warning');
            return;
        }
        if (!newAuthForm.reason.trim()) {
            Swal.fire('Attention', 'Le motif est obligatoire.', 'warning');
            return;
        }
        if (!newAuthForm.protectionPassword) {
            Swal.fire('Attention', 'Veuillez saisir votre code secret de protection.', 'warning');
            return;
        }

        try {
            setSaving(true);
            const response = await axios.post(
                `${API_URL}/system-settings/exceptional-authorizations`,
                newAuthForm,
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Autorisation Accordée',
                    text: 'L\'autorisation exceptionnelle a été créée avec succès.',
                    timer: 2500,
                    showConfirmButton: false
                });
                setIsCreateAuthOpen(false);
                setNewAuthForm({
                    userId: '',
                    actionCodes: [],
                    modules: [],
                    bypassSecretCode: false,
                    bypassMaintenance: false,
                    enableDisabledFeatures: false,
                    permanent: false,
                    startAt: '',
                    expiresAt: '',
                    reason: '',
                    protectionPassword: ''
                });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur création autorisation exceptionnelle:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la création de l\'autorisation.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Revoke Exceptional Authorization
    const handleRevokeAuthSubmit = async (e) => {
        e.preventDefault();
        if (!revokeAuthModal.protectionPassword) {
            Swal.fire('Attention', 'Le code secret est obligatoire.', 'warning');
            return;
        }

        try {
            setSaving(true);
            const response = await axios.post(
                `${API_URL}/system-settings/exceptional-authorizations/${revokeAuthModal.authId}/revoke`,
                {
                    protectionPassword: revokeAuthModal.protectionPassword,
                    reason: revokeAuthModal.reason
                },
                getAuthHeaders()
            );

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Autorisation Révoquée',
                    text: 'L\'autorisation a été immédiatement révoquée.',
                    timer: 2000,
                    showConfirmButton: false
                });
                setRevokeAuthModal({ isOpen: false, authId: null, reason: '', protectionPassword: '' });
                loadAllData();
            }
        } catch (err) {
            console.error('Erreur révocation autorisation:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la révocation.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="sdc-loading-container">
                <div className="sdc-spinner" />
                <p>Chargement de la matrice de gouvernance & sécurité des actions...</p>
            </div>
        );
    }

    return (
        <div className="sdc-wrapper">
            {/* Header Main Card */}
            <div className="sdc-header-card">
                <div className="sdc-header-left">
                    <div className="sdc-header-icon">🛡️</div>
                    <div className="sdc-header-title">
                        <h2>Gestion des Actions & Données Sensibles</h2>
                        <p>Centre autoritaire de contrôle d'accès, mode maintenance, purge de sessions et privilèges dérogatoires.</p>
                    </div>
                </div>

                <div className="sdc-header-kpis">
                    <div className="sdc-kpi-chip">
                        <span className="sdc-kpi-val">{actions.length}</span>
                        <span className="sdc-kpi-lbl">Actions Centralisées</span>
                    </div>
                    <div className="sdc-kpi-chip">
                        <span className={`sdc-kpi-val ${platformControl.maintenanceMode ? 'text-danger' : 'text-success'}`}>
                            {platformControl.maintenanceMode ? 'ACTIVE' : 'NORMALE'}
                        </span>
                        <span className="sdc-kpi-lbl">Plateforme</span>
                    </div>
                    <div className="sdc-kpi-chip">
                        <span className="sdc-kpi-val text-amber">
                            {exceptionalAuths.filter(a => a.isActive && (a.permanent || (a.expiresAt && new Date(a.expiresAt) > new Date()))).length}
                        </span>
                        <span className="sdc-kpi-lbl">Dérogations Actives</span>
                    </div>
                </div>
            </div>

            {/* Platform Global Controls Card */}
            <div className="sdc-platform-card">
                <div className="sdc-platform-block">
                    <div className="sdc-platform-info">
                        <div className={`sdc-platform-badge ${platformControl.maintenanceMode ? 'badge-danger' : 'badge-success'}`}>
                            {platformControl.maintenanceMode ? '🚨 Mode Maintenance Global Actif' : '🟢 Plateforme en Service Standard'}
                        </div>
                        <h3>Contrôle Global du Système & Maintenance</h3>
                        <p>
                            Basculez l'ensemble du site en maintenance d'urgence. Seuls les administrateurs avec droit d'exception pourront continuer la navigation.
                        </p>
                    </div>

                    <div className="sdc-platform-action">
                        <button
                            className={`sdc-btn-maint ${platformControl.maintenanceMode ? 'active' : ''}`}
                            onClick={handleToggleMaintenanceMode}
                            disabled={togglingMaint}
                        >
                            {togglingMaint ? 'Mise à jour...' : platformControl.maintenanceMode ? '🟢 Relever le Mode Maintenance' : '🚨 Basculer en Mode Maintenance Global'}
                        </button>
                    </div>
                </div>

                <div className="sdc-platform-block">
                    <div className="sdc-platform-info">
                        <div className={`sdc-platform-badge ${platformControl.paiementEcheanceMaintenance ? 'badge-danger' : 'badge-success'}`}>
                            {platformControl.paiementEcheanceMaintenance ? '🚨 Maintenance Paiements & Échéances' : '🟢 Module Paiements Opérationnel'}
                        </div>
                        <h3>Maintenance Module Paiements & Échéances</h3>
                        <p>
                            Basculez uniquement la gestion des paiements et des échéances en mode maintenance. Les boutons d'ajout, modification et programmation d'échéances seront bloqués dans les dossiers.
                        </p>
                    </div>

                    <div className="sdc-platform-action">
                        <button
                            className={`sdc-btn-maint ${platformControl.paiementEcheanceMaintenance ? 'active' : ''}`}
                            onClick={handleTogglePaiementMaintenanceMode}
                            disabled={togglingMaint}
                        >
                            {togglingMaint ? 'Mise à jour...' : platformControl.paiementEcheanceMaintenance ? '🟢 Relever la Maintenance Paiements' : '🚨 Verrouiller Paiements & Échéances'}
                        </button>
                    </div>
                </div>

                <div className="sdc-platform-block">
                    <div className="sdc-platform-info">
                        <div className="sdc-platform-badge badge-warning">
                            🧹 Invalidation & Purge de Tokens
                        </div>
                        <h3>Réinitialisation des Sessions Actives</h3>
                        <p>
                            Force tous les utilisateurs connectés à se reconnecter immédiatement en révoquant l'ensemble des jetons d'accès et de rafraîchissement.
                        </p>
                    </div>

                    <div className="sdc-platform-action">
                        {platformControl.lastFlushedAt && (
                            <div className="sdc-flush-time">
                                ⏱️ Dernier nettoyage : <strong>{new Date(platformControl.lastFlushedAt).toLocaleString('fr-FR')}</strong>
                            </div>
                        )}
                        <button
                            className="sdc-btn-flush"
                            onClick={handleFlushAllSessions}
                            disabled={flushingSessions}
                        >
                            {flushingSessions ? 'Purge en cours...' : '🚨 Vider toutes les sessions actives'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="sdc-tabs-nav">
                <button
                    className={`sdc-tab-btn ${activeTab === 'actions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('actions')}
                >
                    ⚡ Catalogue & Règles d'Actions ({filteredActions.length})
                </button>
                <button
                    className={`sdc-tab-btn ${activeTab === 'exceptional' ? 'active' : ''}`}
                    onClick={() => setActiveTab('exceptional')}
                >
                    🛡️ Autorisations Exceptionnelles ({exceptionalAuths.length})
                </button>
                <button
                    className={`sdc-tab-btn ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}
                >
                    🔑 Code Secret de Protection
                </button>
                <button
                    className={`sdc-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                >
                    📋 Historique des Modifications ({history.length})
                </button>
            </div>

            {/* TAB 1 : ACTIONS CATALOG & RULES */}
            {activeTab === 'actions' && (
                <>
                    {/* Search & Toolbar */}
                    <div className="sdc-toolbar">
                        <div className="sdc-search-box">
                            <span className="sdc-search-icon">🔍</span>
                            <input
                                type="text"
                                placeholder="Rechercher une action par nom, actionCode ou description..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="sdc-filter-group">
                            <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                                <option value="ALL">Tous les modules</option>
                                <option value="clients">Clients</option>
                                <option value="dossiers">Dossiers</option>
                                <option value="paiements">Paiements</option>
                                <option value="stock">Stock</option>
                                <option value="achats">Achats</option>
                                <option value="finance">Finance</option>
                                <option value="utilisateurs">Utilisateurs</option>
                                <option value="documents">Documents</option>
                                <option value="systeme">Système</option>
                            </select>

                            <select value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
                                <option value="ALL">Tous les niveaux</option>
                                <option value="NORMAL">NORMAL</option>
                                <option value="SENSIBLE">SENSIBLE</option>
                                <option value="TRES_SENSIBLE">TRÈS SENSIBLE</option>
                                <option value="CRITIQUE">CRITIQUE</option>
                            </select>

                            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                <option value="ALL">Tous les types</option>
                                <option value="VIEW">VIEW</option>
                                <option value="CREATE">CREATE</option>
                                <option value="EDIT">EDIT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="RESTORE">RESTORE</option>
                                <option value="EXPORT">EXPORT</option>
                                <option value="DOWNLOAD">DOWNLOAD</option>
                            </select>
                        </div>
                    </div>

                    {/* Actions Grid */}
                    <div className="sdc-actions-grid">
                        {filteredActions.map((action) => {
                            const isCriticalModuleAction = ['SETTINGS_UPDATE', 'ARCHIVE_RESTORE', 'AUDIT_LOGS_VIEW'].includes(action.actionCode);
                            return (
                                <div key={action.actionCode} className="sdc-action-card">
                                    <div className="sdc-action-card-head">
                                        <div className="sdc-action-code-badge">
                                            <span>{action.actionCode}</span>
                                            {isCriticalModuleAction && (
                                                <span className="sdc-critical-tag">
                                                    🔒 MODULE CRITIQUE
                                                </span>
                                            )}
                                        </div>

                                        <span className={`sdc-level-tag level-${action.level.toLowerCase()}`}>
                                            {action.level}
                                        </span>
                                    </div>

                                    <div className="sdc-action-card-body">
                                        <h4>{action.label}</h4>
                                        <p>{action.description || 'Aucune description fournie.'}</p>

                                        <div className="sdc-action-meta">
                                            <span className="sdc-meta-item">📁 Module: <strong>{action.module}</strong></span>
                                            <span className="sdc-meta-item">🏷️ Type: <strong>{action.type}</strong></span>
                                        </div>

                                        {/* Quick Controls */}
                                        <div className="sdc-quick-controls">
                                            <div className="sdc-ctrl-row">
                                                <span>Fonctionnalité Activée :</span>
                                                <div className="sdc-toggle-control" onClick={() => handleQuickToggleAction(action, 'enabled', !action.enabled)}>
                                                    <div className={`sdc-toggle-track ${action.enabled ? 'checked' : ''}`}>
                                                        <div className="sdc-toggle-thumb" />
                                                    </div>
                                                    <span className={`sdc-toggle-status-text ${action.enabled ? 'text-success' : 'text-slate'}`}>
                                                        {action.enabled ? 'ON' : 'OFF'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="sdc-ctrl-row">
                                                <span>Exiger Code Secret :</span>
                                                <div className="sdc-toggle-control" onClick={() => !isCriticalModuleAction && handleQuickToggleAction(action, 'requirePassword', !action.requirePassword)}>
                                                    <div className={`sdc-toggle-track ${isCriticalModuleAction ? 'checked' : action.requirePassword ? 'checked' : ''}`}>
                                                        <div className="sdc-toggle-thumb" />
                                                    </div>
                                                    <span className={`sdc-toggle-status-text ${(isCriticalModuleAction || action.requirePassword) ? 'text-blue' : 'text-slate'}`}>
                                                        {isCriticalModuleAction ? 'Toujours (Critique)' : action.requirePassword ? 'Code' : 'Libre'}
                                                    </span>
                                                </div>
                                            </div>

                                            {action.deletionMode !== 'NOT_APPLICABLE' && (
                                                <div className="sdc-ctrl-row">
                                                    <span>Mode Suppression :</span>
                                                    <select
                                                        className="sdc-select-deletion-mode"
                                                        value={(action.enabled === false || action.deletionMode === 'NOT_ALLOWED') ? 'NOT_ALLOWED' : action.deletionMode}
                                                        onChange={(e) => handleQuickToggleAction(action, 'deletionMode', e.target.value)}
                                                    >
                                                        <option value="NOT_ALLOWED">⛔ Non Autorisée (Blocage total)</option>
                                                        <option value="SOFT_DELETE">📦 Soft Delete (Archivage)</option>
                                                        <option value="HARD_DELETE">🚨 Hard Delete (Suppression définitive)</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="sdc-action-card-foot">
                                        <span className="sdc-session-duration-tag">
                                            ⏱️ Session: <strong>{action.unlockDurationMinutes || 15} min</strong>
                                        </span>

                                        <button
                                            className="sdc-btn-edit-details"
                                            onClick={() => setEditActionModal({ isOpen: true, action: { ...action } })}
                                        >
                                            ⚙️ Modifier Règles
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* TAB 2 : EXCEPTIONAL AUTHORIZATIONS (DÉLÉGATION DE PRIVILÈGES) */}
            {activeTab === 'exceptional' && (
                <div className="sdc-exceptional-container">
                    <div className="sdc-exceptional-header">
                        <div className="sdc-exceptional-title-area">
                            <h3>Délégation & Autorisations Exceptionnelles</h3>
                            <p>
                                Accordez temporairement ou définitivement des privilèges dérogatoires (accès sans code secret, réactivation de fonctions) avec traçabilité d'audit.
                            </p>
                        </div>

                        <button
                            className="sdc-btn-add-exceptional"
                            onClick={() => setIsCreateAuthOpen(true)}
                        >
                            ➕ Accorder une Autorisation Exceptionnelle
                        </button>
                    </div>

                    {/* Filter Status & Purge */}
                    <div className="sdc-status-filters-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {['ALL', 'active', 'expired', 'revoked'].map(st => (
                                <button
                                    key={st}
                                    onClick={() => setAuthFilterStatus(st)}
                                    className={`sdc-status-filter-btn ${authFilterStatus === st ? 'active' : ''} btn-${st}`}
                                >
                                    {st === 'ALL' ? 'Toutes' : st === 'active' ? '🟢 Actives' : st === 'expired' ? '🟧 Expirées' : '🔴 Révoquées'}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handlePurgeRevokedAuths}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: '1px solid #f87171',
                                backgroundColor: '#fff1f2',
                                color: '#991b1b',
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            🗑️ Purger les Révoquées
                        </button>
                    </div>

                    {/* Exceptional Auths List */}
                    <div className="sdc-auth-list-container">
                        {filteredExceptionalAuths.length === 0 ? (
                            <div className="sdc-auth-empty-state">
                                Aucun privilège dérogatoire ou autorisation exceptionnelle correspondant.
                            </div>
                        ) : (
                            filteredExceptionalAuths.map(auth => {
                                const now = new Date();
                                const isCurrentlyActive = auth.isActive && (auth.permanent || (auth.expiresAt && new Date(auth.expiresAt) > now));
                                const isCurrentlyExpired = auth.isActive && !auth.permanent && auth.expiresAt && new Date(auth.expiresAt) <= now;

                                return (
                                    <div key={auth._id} className="sdc-auth-card">
                                        <div className="sdc-auth-card-main">
                                            <div className="sdc-auth-user-header">
                                                <span className="sdc-auth-user-title">
                                                    👤 {auth.userId?.prenom} {auth.userId?.nom} ({auth.userId?.mail || 'Utilisateur'})
                                                </span>
                                                <span className={`sdc-auth-status-pill ${isCurrentlyActive ? 'pill-active' : isCurrentlyExpired ? 'pill-expired' : 'pill-revoked'}`}>
                                                    {isCurrentlyActive ? '🟢 ACTIVE' : isCurrentlyExpired ? '🟧 EXPIRÉE' : '🔴 RÉVOQUÉE'}
                                                </span>
                                            </div>

                                            <div className="sdc-auth-badges-row">
                                                {auth.bypassSecretCode && (
                                                    <span className="sdc-auth-badge badge-bypass">
                                                        🔑 Bypass Code Secret
                                                    </span>
                                                )}
                                                {auth.bypassMaintenance && (
                                                    <span className="sdc-auth-badge badge-maint-bypass">
                                                        🚨 Bypass Mode Maintenance
                                                    </span>
                                                )}
                                                {auth.enableDisabledFeatures && (
                                                    <span className="sdc-auth-badge badge-override">
                                                        🔓 Override Désactivations
                                                    </span>
                                                )}
                                                {Array.isArray(auth.actionCodes) && auth.actionCodes.map(code => (
                                                    <span key={code} className="sdc-auth-badge badge-code">
                                                        ⚡ {code}
                                                    </span>
                                                ))}
                                                {Array.isArray(auth.modules) && auth.modules.map(mod => (
                                                    <span key={mod} className="sdc-auth-badge badge-module">
                                                        📁 Module: {mod}
                                                    </span>
                                                ))}
                                            </div>

                                            <div className="sdc-auth-period-text">
                                                ⏱️ <strong>Période :</strong> {auth.permanent ? '♾️ Permanente (Sans expiration)' : `Du ${new Date(auth.startAt).toLocaleString('fr-FR')} au ${new Date(auth.expiresAt).toLocaleString('fr-FR')}`}
                                            </div>

                                            <div className="sdc-auth-reason-box">
                                                📝 <strong>Motif :</strong> "{auth.reason}"
                                            </div>

                                            <div className="sdc-auth-granted-footer">
                                                Accordée par <strong>{auth.grantedBy?.prenom} {auth.grantedBy?.nom}</strong> le {new Date(auth.createdAt).toLocaleString('fr-FR')}
                                            </div>
                                        </div>

                                        {isCurrentlyActive && (
                                            <div className="sdc-auth-card-action">
                                                <button
                                                    onClick={() => setRevokeAuthModal({ isOpen: true, authId: auth._id, reason: '', protectionPassword: '' })}
                                                    className="sdc-btn-revoke-auth"
                                                >
                                                    🚫 Révoquer
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* TAB 3 : CHANGE PASSWORD */}
            {activeTab === 'password' && (
                <div className="sdc-password-card">
                    <h3>🔑 Modification du Code Secret de Protection</h3>
                    <p>
                        Le code secret de protection sécurise l'exécution des actions sensibles et empêche la modification non autorisée des politiques de sécurité.
                    </p>

                    <form onSubmit={handleChangeProtectionPassword} className="sdc-pwd-form">
                        <div className="sdc-form-group">
                            <label>Code Secret Actuel :</label>
                            <input
                                type="password"
                                placeholder="Entrez le code secret actuel"
                                value={pwdForm.currentPassword}
                                onChange={(e) => setPwdForm({ ...pwdForm, currentPassword: e.target.value })}
                                required
                            />
                        </div>

                        <div className="sdc-form-group">
                            <label>Nouveau Code Secret :</label>
                            <input
                                type="password"
                                placeholder="Au moins 6 caractères"
                                value={pwdForm.newPassword}
                                onChange={(e) => setPwdForm({ ...pwdForm, newPassword: e.target.value })}
                                required
                            />
                        </div>

                        <div className="sdc-form-group">
                            <label>Confirmer le Nouveau Code Secret :</label>
                            <input
                                type="password"
                                placeholder="Répétez le nouveau code secret"
                                value={pwdForm.confirmPassword}
                                onChange={(e) => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })}
                                required
                            />
                        </div>

                        <button type="submit" className="sdc-save-btn" disabled={changingPwd}>
                            {changingPwd ? 'Mise à jour...' : 'Mettre à Jour le Code Secret'}
                        </button>
                    </form>
                </div>
            )}

            {/* TAB 4 : HISTORY */}
            {activeTab === 'history' && (
                <div className="sdc-history-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0 }}>📋 Historique des Modifications des Politiques</h3>
                        <button
                            type="button"
                            onClick={handleClearHistory}
                            disabled={history.length === 0}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                backgroundColor: history.length === 0 ? '#f1f5f9' : '#fff1f2',
                                color: history.length === 0 ? '#94a3b8' : '#991b1b',
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                cursor: history.length === 0 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            🧹 Vider l'historique
                        </button>
                    </div>
                    <div className="sdc-table-wrapper">
                        <table className="sdc-table">
                            <thead>
                                <tr>
                                    <th>Date & Heure</th>
                                    <th>ActionCode</th>
                                    <th>Administrateur</th>
                                    <th>Détails & Modifications</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="sdc-table-empty">
                                            Aucune modification enregistrée dans le journal.
                                        </td>
                                    </tr>
                                ) : (
                                    history.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{new Date(item.updatedAt).toLocaleString('fr-FR')}</td>
                                            <td><strong>{item.actionCode}</strong></td>
                                            <td>{item.updatedBy}</td>
                                            <td>
                                                <pre className="sdc-json-changes">
                                                    {JSON.stringify(item.changes, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MODAL : CREATE EXCEPTIONAL AUTHORIZATION */}
            {isCreateAuthOpen && (
                <div className="sdc-modal-overlay">
                    <div className="sdc-modal-card modal-large">
                        <div className="sdc-modal-header">
                            <h3>🛡️ Accorder une Autorisation Exceptionnelle</h3>
                            <button className="sdc-modal-close" onClick={() => setIsCreateAuthOpen(false)}>✕</button>
                        </div>

                        <form onSubmit={handleCreateExceptionalAuthSubmit} className="sdc-modal-form-content">
                            <div className="sdc-field-group">
                                <label>Utilisateur Bénéficiaire * :</label>
                                <select
                                    className="sdc-field-input"
                                    value={newAuthForm.userId}
                                    onChange={(e) => setNewAuthForm({ ...newAuthForm, userId: e.target.value })}
                                    required
                                >
                                    <option value="">Sélectionner un utilisateur...</option>
                                    {usersList.map(u => (
                                        <option key={u._id} value={u._id}>
                                            {u.prenom} {u.nom} ({u.typeUser || 'Rôle'}) - {u.mail}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="sdc-field-group">
                                <label>Privilèges de Dérogation :</label>
                                <div className="sdc-checkbox-option-list">
                                    <label className="sdc-checkbox-option-label">
                                        <input
                                            type="checkbox"
                                            checked={newAuthForm.bypassSecretCode}
                                            onChange={(e) => setNewAuthForm({ ...newAuthForm, bypassSecretCode: e.target.checked })}
                                        />
                                        🔑 <strong>Bypass Code Secret</strong> (Accès direct sans demander le mot de passe secret)
                                    </label>
                                    <label className="sdc-checkbox-option-label">
                                        <input
                                            type="checkbox"
                                            checked={newAuthForm.bypassMaintenance}
                                            onChange={(e) => setNewAuthForm({ ...newAuthForm, bypassMaintenance: e.target.checked })}
                                        />
                                        🚨 <strong>Bypass Mode Maintenance</strong> (Accès direct à la plateforme en mode maintenance)
                                    </label>
                                    <label className="sdc-checkbox-option-label">
                                        <input
                                            type="checkbox"
                                            checked={newAuthForm.enableDisabledFeatures}
                                            onChange={(e) => setNewAuthForm({ ...newAuthForm, enableDisabledFeatures: e.target.checked })}
                                        />
                                        🔓 <strong>Activer les fonctionnalités désactivées</strong> (Accès dérogatoire aux actions désactivées)
                                    </label>
                                </div>
                            </div>

                            <div className="sdc-field-group">
                                <div className="sdc-field-header-row">
                                    <label>Actions Sensibles Concernées :</label>
                                    <button
                                        type="button"
                                        className="sdc-select-all-btn"
                                        onClick={() => {
                                            const allCodes = actions.map(a => a.actionCode);
                                            const isAllSelected = actions.length > 0 && actions.every(a => newAuthForm.actionCodes.includes(a.actionCode));
                                            setNewAuthForm({
                                                ...newAuthForm,
                                                actionCodes: isAllSelected ? [] : allCodes
                                            });
                                        }}
                                    >
                                        {actions.length > 0 && actions.every(a => newAuthForm.actionCodes.includes(a.actionCode))
                                            ? '☐ Tout Décocher'
                                            : '☑ Tout Sélectionner'}
                                    </button>
                                </div>
                                <div className="sdc-actions-selection-box">
                                    {actions.map(act => (
                                        <label key={act.actionCode} className={`sdc-action-checkbox-item ${newAuthForm.actionCodes.includes(act.actionCode) ? 'checked' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={newAuthForm.actionCodes.includes(act.actionCode)}
                                                onChange={(e) => {
                                                    const exists = newAuthForm.actionCodes.includes(act.actionCode);
                                                    const nextCodes = exists ? newAuthForm.actionCodes.filter(c => c !== act.actionCode) : [...newAuthForm.actionCodes, act.actionCode];
                                                    setNewAuthForm({ ...newAuthForm, actionCodes: nextCodes });
                                                }}
                                            />
                                            {act.actionCode}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="sdc-field-group">
                                <label>Durée de Validité * :</label>
                                <div className="sdc-radio-validity-group">
                                    <label className="sdc-radio-label">
                                        <input
                                            type="radio"
                                            name="validityType"
                                            checked={!newAuthForm.permanent}
                                            onChange={() => setNewAuthForm({ ...newAuthForm, permanent: false })}
                                        /> Temporaire (Définir des dates)
                                    </label>
                                    <label className="sdc-radio-label">
                                        <input
                                            type="radio"
                                            name="validityType"
                                            checked={newAuthForm.permanent}
                                            onChange={() => setNewAuthForm({ ...newAuthForm, permanent: true })}
                                        /> ♾️ Permanente (Sans expiration)
                                    </label>
                                </div>

                                {!newAuthForm.permanent && (
                                    <div className="sdc-dates-flex-row">
                                        <div className="sdc-date-col">
                                            <label className="sdc-sublabel">Date de Début :</label>
                                            <input
                                                type="datetime-local"
                                                className="sdc-field-input"
                                                value={newAuthForm.startAt}
                                                onChange={(e) => setNewAuthForm({ ...newAuthForm, startAt: e.target.value })}
                                            />
                                        </div>
                                        <div className="sdc-date-col">
                                            <label className="sdc-sublabel">Date d'Expiration :</label>
                                            <input
                                                type="datetime-local"
                                                className="sdc-field-input"
                                                value={newAuthForm.expiresAt}
                                                onChange={(e) => setNewAuthForm({ ...newAuthForm, expiresAt: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="sdc-field-group">
                                <label>Motif Obligatoire * :</label>
                                <textarea
                                    className="sdc-field-input"
                                    rows="2"
                                    placeholder="Ex: Autorisation temporaire accordée pour l'audit financier du 30/09/2026."
                                    value={newAuthForm.reason}
                                    onChange={(e) => setNewAuthForm({ ...newAuthForm, reason: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="sdc-field-group">
                                <label>Code Secret de Protection * :</label>
                                <input
                                    type="password"
                                    className="sdc-field-input"
                                    placeholder="Saisissez votre code secret actuel pour valider l'octroi"
                                    value={newAuthForm.protectionPassword}
                                    onChange={(e) => setNewAuthForm({ ...newAuthForm, protectionPassword: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="sdc-modal-actions-footer">
                                <button type="button" className="sdc-btn-edit-details" onClick={() => setIsCreateAuthOpen(false)}>
                                    Annuler
                                </button>
                                <button type="submit" className="sdc-save-btn" disabled={saving}>
                                    {saving ? 'Création...' : 'Valider & Accorder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL : REVOKE EXCEPTIONAL AUTHORIZATION */}
            {revokeAuthModal.isOpen && (
                <div className="sdc-modal-overlay">
                    <div className="sdc-modal-card modal-small">
                        <div className="sdc-modal-header">
                            <h3>🚫 Révoquer l'Autorisation Exceptionnelle</h3>
                            <button className="sdc-modal-close" onClick={() => setRevokeAuthModal({ isOpen: false, authId: null, reason: '', protectionPassword: '' })}>✕</button>
                        </div>

                        <form onSubmit={handleRevokeAuthSubmit} className="sdc-modal-form-content">
                            <p className="sdc-modal-warning-text">
                                Êtes-vous sûr de vouloir révoquer immédiatement ce privilège dérogatoire ?
                            </p>

                            <div className="sdc-field-group">
                                <label>Motif de la Révocation :</label>
                                <input
                                    type="text"
                                    className="sdc-field-input"
                                    placeholder="Ex: Fin anticipée de la mission d'audit"
                                    value={revokeAuthModal.reason}
                                    onChange={(e) => setRevokeAuthModal({ ...revokeAuthModal, reason: e.target.value })}
                                />
                            </div>

                            <div className="sdc-field-group">
                                <label>Code Secret de Confirmation * :</label>
                                <input
                                    type="password"
                                    className="sdc-field-input"
                                    placeholder="Code secret de protection"
                                    value={revokeAuthModal.protectionPassword}
                                    onChange={(e) => setRevokeAuthModal({ ...revokeAuthModal, protectionPassword: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="sdc-modal-actions-footer">
                                <button type="button" className="sdc-btn-edit-details" onClick={() => setRevokeAuthModal({ isOpen: false, authId: null, reason: '', protectionPassword: '' })}>
                                    Annuler
                                </button>
                                <button type="submit" className="sdc-btn-flush" disabled={saving}>
                                    {saving ? 'Révocation...' : 'Révoquer Immédiatement'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL : EDIT DETAILED ACTION RULES */}
            {editActionModal.isOpen && (
                <div className="sdc-modal-overlay">
                    <div className="sdc-modal-card">
                        <div className="sdc-modal-header">
                            <h3>⚙️ Configuration de l'Action "{editActionModal.action.label}"</h3>
                            <button className="sdc-modal-close" onClick={() => setEditActionModal({ isOpen: false, action: null })}>✕</button>
                        </div>

                        <form onSubmit={handleSaveDetailedAction}>
                            <div className="sdc-modal-body">
                                <div className="sdc-field-group">
                                    <label>Nom / Libellé de l'Action :</label>
                                    <input
                                        type="text"
                                        className="sdc-field-input"
                                        value={editActionModal.action.label}
                                        onChange={(e) => setEditActionModal({
                                            ...editActionModal,
                                            action: { ...editActionModal.action, label: e.target.value }
                                        })}
                                        required
                                    />
                                </div>

                                <div className="sdc-field-group">
                                    <label>Description :</label>
                                    <textarea
                                        className="sdc-field-input"
                                        rows="2"
                                        value={editActionModal.action.description || ''}
                                        onChange={(e) => setEditActionModal({
                                            ...editActionModal,
                                            action: { ...editActionModal.action, description: e.target.value }
                                        })}
                                    />
                                </div>

                                <div className="sdc-field-grid">
                                    <div className="sdc-field-group">
                                        <label>Niveau de Criticité :</label>
                                        <select
                                            className="sdc-field-input"
                                            value={editActionModal.action.level}
                                            onChange={(e) => setEditActionModal({
                                                ...editActionModal,
                                                action: { ...editActionModal.action, level: e.target.value }
                                            })}
                                        >
                                            <option value="NORMAL">NORMAL</option>
                                            <option value="SENSIBLE">SENSIBLE</option>
                                            <option value="TRES_SENSIBLE">TRÈS_SENSIBLE</option>
                                            <option value="CRITIQUE">CRITIQUE</option>
                                        </select>
                                    </div>

                                    <div className="sdc-field-group">
                                        <label>Durée de Session (minutes) :</label>
                                        <select
                                            className="sdc-field-input"
                                            value={editActionModal.action.unlockDurationMinutes || 15}
                                            onChange={(e) => setEditActionModal({
                                                ...editActionModal,
                                                action: { ...editActionModal.action, unlockDurationMinutes: Number(e.target.value) }
                                            })}
                                        >
                                            <option value={0}>0 min (Toujours demander)</option>
                                            <option value={5}>5 min</option>
                                            <option value={10}>10 min</option>
                                            <option value={15}>15 min</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="sdc-field-group sdc-mb-20">
                                    <label>Code Secret de Confirmation :</label>
                                    <input
                                        type="password"
                                        className="sdc-field-input"
                                        placeholder="Saisissez votre code secret actuel pour valider"
                                        value={confirmPasswordInput}
                                        onChange={(e) => setConfirmPasswordInput(e.target.value)}
                                    />
                                </div>

                                <div className="sdc-modal-actions-footer">
                                    <button type="button" className="sdc-btn-edit-details" onClick={() => setEditActionModal({ isOpen: false, action: null })}>
                                        Annuler
                                    </button>
                                    <button type="submit" className="sdc-save-btn" disabled={saving}>
                                        {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SensitiveDeleteConfig;
